import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { v4 as uuidv4 } from 'uuid';

// Um "bem" pode ser um veículo ou uma residência/imóvel. Cada registro guarda
// `kind`; os cadastros antigos (sem o campo) contam como 'vehicle' por padrão,
// então nada precisa migrar.
const ASSET_KINDS = [
  { value: 'vehicle',   label: 'Veículo',    icon: 'fa-car',       defaultCategory: 'transp' },
  { value: 'residence', label: 'Residência', icon: 'fa-house',     defaultCategory: 'moradia' },
];

const VEHICLE_TYPES = [
  { value: 'carro',    label: 'Carro',          icon: 'fa-car' },
  { value: 'moto',     label: 'Moto',           icon: 'fa-motorcycle' },
  { value: 'caminhao', label: 'Caminhão',       icon: 'fa-truck' },
  { value: 'van',      label: 'Van/Utilitário', icon: 'fa-van-shuttle' },
  { value: 'outro',    label: 'Outro',          icon: 'fa-circle-dot' },
];

const RESIDENCE_TYPES = [
  { value: 'casa',        label: 'Casa',           icon: 'fa-house' },
  { value: 'apartamento', label: 'Apartamento',    icon: 'fa-building' },
  { value: 'terreno',     label: 'Terreno',        icon: 'fa-mountain-sun' },
  { value: 'comercio',    label: 'Comércio/Loja',  icon: 'fa-store' },
  { value: 'chacara',     label: 'Chácara/Sítio',  icon: 'fa-tree' },
  { value: 'outro',       label: 'Outro',          icon: 'fa-house-chimney' },
];

const ASSET_COLORS = ['#0d1b2a','#1D9E75','#185FA5','#8A05BE','var(--gold)','#e24b4a'];

const kindOf   = (asset) => (asset?.kind === 'residence' ? 'residence' : 'vehicle');
const kindMeta = (kind) => ASSET_KINDS.find(k => k.value === kind) || ASSET_KINDS[0];
const typesFor = (kind) => (kind === 'residence' ? RESIDENCE_TYPES : VEHICLE_TYPES);
function typeMeta(kind, type) {
  const list = typesFor(kind);
  return list.find(t => t.value === type) || list[list.length - 1];
}

function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function formatCurrency(v) {
  return v?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00';
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  return Math.round((target - today) / 86400000);
}

// ── Modal de Bem (veículo ou residência) ───────────────────────────────────────
function AssetModal({ asset, defaultKind, onSave, onClose }) {
  const editing = !!asset;
  const initialKind = asset ? kindOf(asset) : (defaultKind || 'vehicle');
  const [form, setForm] = useState({
    kind:  initialKind,
    name:  asset?.name  || '',
    type:  asset?.type  || typesFor(initialKind)[0].value,
    color: asset?.color || '#185FA5',
    plate: asset?.plate || '',
    year:  asset?.year  || '',
    address: asset?.address || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const changeKind = (kind) => setForm(f => ({ ...f, kind, type: typesFor(kind)[0].value }));
  const isResidence = form.kind === 'residence';
  const valid = form.name.trim().length > 0;
  const km = kindMeta(form.kind);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 16, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', margin: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
            {editing ? `Editar ${km.label}` : `${km.label === 'Residência' ? 'Nova' : 'Novo'} ${km.label}`}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18 }}><i className="fas fa-times"></i></button>
        </div>

        {/* Tipo de bem — só na criação (mudar depois bagunçaria o histórico) */}
        {!editing && (
          <div className="form-group">
            <label className="form-label">É um veículo ou uma residência?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {ASSET_KINDS.map(k => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => changeKind(k.value)}
                  className={`btn btn-sm ${form.kind === k.value ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  <i className={`fas ${k.icon}`}></i> {k.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Nome *</label>
          <input
            className="form-control"
            placeholder={isResidence ? 'Ex: Casa da praia, Apê centro...' : 'Ex: Civic 2021, Honda Biz...'}
            value={form.name}
            onChange={e => set('name', e.target.value)}
            autoFocus
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-control" value={form.type} onChange={e => set('type', e.target.value)}>
              {typesFor(form.kind).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {isResidence ? (
            <div className="form-group">
              <label className="form-label">Endereço/apelido (opcional)</label>
              <input className="form-control" placeholder="Bairro, cidade..." value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Ano</label>
              <input className="form-control" placeholder="Ex: 2021" value={form.year} onChange={e => set('year', e.target.value)} maxLength={4} />
            </div>
          )}
        </div>

        {!isResidence && (
          <div className="form-group">
            <label className="form-label">Placa (opcional)</label>
            <input className="form-control" placeholder="ABC-1234" value={form.plate} onChange={e => set('plate', e.target.value.toUpperCase())} maxLength={8} />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Cor</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {ASSET_COLORS.map(c => (
              <div key={c} onClick={() => set('color', c)} style={{ width: 30, height: 30, borderRadius: '50%', background: c, cursor: 'pointer', border: form.color === c ? '3px solid var(--gold)' : '3px solid transparent', transition: 'all 0.15s' }} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} className="btn btn-ghost" style={{ flex: 1 }}>Cancelar</button>
          <button onClick={() => valid && onSave(form)} className="btn btn-primary" style={{ flex: 2 }} disabled={!valid}>
            <i className="fas fa-check"></i> {editing ? 'Salvar' : `Criar ${km.label.toLowerCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de Manutenção ────────────────────────────────────────────────────────
function MaintenanceModal({ maintenance, assetId, assets, accounts, categories, onSave, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const preselected = assetId || maintenance?.vehicleId || (assets[0]?.id ?? '');
  const kindForCategory = (aid) => kindMeta(kindOf(assets.find(a => a.id === aid))).defaultCategory;
  const [form, setForm] = useState({
    vehicleId:   preselected,
    description: maintenance?.description || '',
    date:        maintenance?.date        || today,
    value:       maintenance?.value       != null ? String(maintenance.value) : '',
    nextDate:    maintenance?.nextDate    || '',
    accountId:   maintenance?.accountId  || (accounts[0]?.id ?? ''),
    categoryId:  maintenance?.categoryId || kindForCategory(preselected),
    createTx:    maintenance ? false : true,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Trocar o bem ajusta a categoria padrão (residência → Moradia, veículo → Transporte)
  const changeAsset = (aid) => setForm(f => ({ ...f, vehicleId: aid, categoryId: kindForCategory(aid) }));
  const valid = form.vehicleId && form.description.trim() && form.date;
  const expCats = categories.filter(c => c.type === 'expense');
  const selectedAsset = assets.find(a => a.id === form.vehicleId);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 16, width: '100%', maxWidth: 480, padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', margin: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{maintenance ? 'Editar Manutenção' : 'Nova Manutenção'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18 }}><i className="fas fa-times"></i></button>
        </div>

        {!assetId && (
          <div className="form-group">
            <label className="form-label">Veículo ou residência *</label>
            <select className="form-control" value={form.vehicleId} onChange={e => changeAsset(e.target.value)}>
              {assets.map(a => <option key={a.id} value={a.id}>{kindMeta(kindOf(a)).label}: {a.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Descrição *</label>
          <input
            className="form-control"
            placeholder={kindOf(selectedAsset) === 'residence' ? 'Ex: Pintura, Encanamento, Ar-condicionado...' : 'Ex: Troca de óleo, Revisão geral, Pneus...'}
            value={form.description}
            onChange={e => set('description', e.target.value)}
            autoFocus
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Data da manutenção *</label>
            <input className="form-control" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-control" type="number" step="0.01" min="0" placeholder="0,00" value={form.value} onChange={e => set('value', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label"><i className="fas fa-calendar-check" style={{ color: 'var(--gold)', marginRight: 6 }}></i>Próxima manutenção (opcional)</label>
          <input className="form-control" type="date" value={form.nextDate} onChange={e => set('nextDate', e.target.value)} min={form.date || today} />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>O sistema vai te lembrar quando essa data chegar.</div>
        </div>

        {!maintenance && parseFloat(form.value) > 0 && (
          <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              <input type="checkbox" checked={form.createTx} onChange={e => set('createTx', e.target.checked)} style={{ width: 16, height: 16 }} />
              Lançar como despesa nos Lançamentos
            </label>
            {form.createTx && (
              <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Conta</label>
                  <select className="form-control" value={form.accountId} onChange={e => set('accountId', e.target.value)}>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Categoria</label>
                  <select className="form-control" value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
                    {expCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} className="btn btn-ghost" style={{ flex: 1 }}>Cancelar</button>
          <button onClick={() => valid && onSave(form)} className="btn btn-primary" style={{ flex: 2 }} disabled={!valid}>
            <i className="fas fa-check"></i> {maintenance ? 'Salvar' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Card de Bem ────────────────────────────────────────────────────────────────
function AssetCard({ asset, maintenance, onEdit, onDelete, onAddMaintenance, onDeleteMaintenance }) {
  const [expanded, setExpanded] = useState(false);
  const kind = kindOf(asset);
  const km = kindMeta(kind);
  const tm = typeMeta(kind, asset.type);
  const records = maintenance.filter(m => m.vehicleId === asset.id).sort((a, b) => b.date.localeCompare(a.date));
  const last = records[0];
  const upcoming = records.filter(m => m.nextDate).sort((a, b) => a.nextDate.localeCompare(b.nextDate))[0];
  const daysLeft = upcoming ? daysUntil(upcoming.nextDate) : null;
  const totalSpent = records.reduce((s, m) => s + (parseFloat(m.value) || 0), 0);

  let nextBadgeColor = '#1d9e75';
  let nextBadgeText = '';
  if (daysLeft !== null) {
    if (daysLeft < 0) { nextBadgeColor = '#e24b4a'; nextBadgeText = `Vencida há ${Math.abs(daysLeft)}d`; }
    else if (daysLeft === 0) { nextBadgeColor = '#e24b4a'; nextBadgeText = 'Hoje!'; }
    else if (daysLeft <= 7) { nextBadgeColor = '#f97316'; nextBadgeText = `${daysLeft}d`; }
    else if (daysLeft <= 30) { nextBadgeColor = 'var(--gold)'; nextBadgeText = `${daysLeft}d`; }
    else { nextBadgeColor = '#1d9e75'; nextBadgeText = `${daysLeft}d`; }
  }

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: asset.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`fas ${tm.icon}`} style={{ fontSize: 22, color: '#fff' }}></i>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {asset.name}
            {kind === 'vehicle' && asset.year && <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>{asset.year}</span>}
            {kind === 'vehicle' && asset.plate && <span style={{ fontSize: 11, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '1px 6px', color: 'var(--text2)', fontWeight: 600, letterSpacing: 1 }}>{asset.plate}</span>}
            <span style={{ fontSize: 10, background: `${km.value === 'residence' ? '#1D9E75' : '#185FA5'}18`, color: km.value === 'residence' ? '#1D9E75' : '#185FA5', borderRadius: 20, padding: '1px 8px', fontWeight: 700 }}>
              <i className={`fas ${km.icon}`} style={{ marginRight: 4 }}></i>{km.label}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{tm.label}{kind === 'residence' && asset.address ? ` · ${asset.address}` : ''}</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            {last && (
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                <i className="fas fa-wrench" style={{ marginRight: 4, color: 'var(--blue)' }}></i>
                Última: {formatDate(last.date)} — {last.description}
              </span>
            )}
            {upcoming && daysLeft !== null && (
              <span style={{ fontSize: 11, background: `${nextBadgeColor}18`, color: nextBadgeColor, borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
                <i className="fas fa-calendar-check" style={{ marginRight: 4 }}></i>
                Próxima: {formatDate(upcoming.nextDate)} {nextBadgeText && `(${nextBadgeText})`}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => onAddMaintenance(asset.id)} title="Nova manutenção" style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-plus" style={{ fontSize: 13 }}></i>
          </button>
          <button onClick={() => onEdit(asset)} title="Editar" style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-pen" style={{ fontSize: 12 }}></i>
          </button>
          <button onClick={() => setExpanded(e => !e)} title="Ver histórico" style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: expanded ? 'var(--navy)' : 'var(--surface2)', cursor: 'pointer', color: expanded ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize: 12 }}></i>
          </button>
        </div>
      </div>

      {records.length > 0 && !expanded && (
        <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{records.length}</span> manutenç{records.length !== 1 ? 'ões' : 'ão'}
          </div>
          {totalSpent > 0 && (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Total gasto: <span style={{ fontWeight: 600, color: 'var(--red)' }}>R$ {formatCurrency(totalSpent)}</span>
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Histórico de Manutenções
            </div>
            {totalSpent > 0 && (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Total: <span style={{ fontWeight: 700, color: 'var(--red)' }}>R$ {formatCurrency(totalSpent)}</span>
              </div>
            )}
          </div>

          {records.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--muted)', fontSize: 13 }}>Nenhuma manutenção registrada ainda.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {records.map(m => (
                <MaintenanceRow key={m.id} record={m} onDelete={onDeleteMaintenance} />
              ))}
            </div>
          )}

          <button onClick={() => onAddMaintenance(asset.id)} className="btn btn-ghost" style={{ width: '100%', marginTop: 10, fontSize: 13 }}>
            <i className="fas fa-plus" style={{ marginRight: 6 }}></i> Nova manutenção
          </button>
        </div>
      )}

      <button onClick={() => { if (window.confirm(`Excluir "${asset.name}" e todo o histórico?`)) onDelete(asset.id); }} style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11, textDecoration: 'underline' }}>
        Excluir {km.label.toLowerCase()}
      </button>
    </div>
  );
}

function MaintenanceRow({ record, onDelete }) {
  const dLeft = record.nextDate ? daysUntil(record.nextDate) : null;
  let nextColor = '#1d9e75';
  if (dLeft !== null) {
    if (dLeft < 0) nextColor = '#e24b4a';
    else if (dLeft <= 7) nextColor = '#f97316';
    else if (dLeft <= 30) nextColor = 'var(--gold)';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className="fas fa-wrench" style={{ fontSize: 13, color: 'var(--blue)' }}></i>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{record.description}</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span><i className="fas fa-calendar" style={{ marginRight: 4 }}></i>{formatDate(record.date)}</span>
          {record.value > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>R$ {formatCurrency(record.value)}</span>}
          {record.nextDate && (
            <span style={{ color: nextColor, fontWeight: 600 }}>
              <i className="fas fa-calendar-check" style={{ marginRight: 4 }}></i>
              Próxima: {formatDate(record.nextDate)}
              {dLeft !== null && ` (${dLeft < 0 ? `atrasada ${Math.abs(dLeft)}d` : dLeft === 0 ? 'hoje' : `${dLeft}d`})`}
            </span>
          )}
        </div>
      </div>
      <button onClick={() => { if (window.confirm('Excluir este registro?')) onDelete(record.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13, padding: '2px 4px', flexShrink: 0 }}>
        <i className="fas fa-trash"></i>
      </button>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function Vehicles() {
  const { state, dispatch } = useApp();
  const { vehicles, vehicleMaintenance, accounts, categories } = state;

  const [assetModal, setAssetModal]             = useState(null); // { asset } | { defaultKind } | null
  const [maintenanceModal, setMaintenanceModal] = useState(null);
  const [filter, setFilter]                     = useState('all'); // all | vehicle | residence

  const saveAsset = (form) => {
    if (assetModal?.asset?.id) {
      dispatch({ type: 'UPDATE_VEHICLE', payload: { ...assetModal.asset, ...form } });
    } else {
      dispatch({ type: 'ADD_VEHICLE', payload: form });
    }
    setAssetModal(null);
  };

  const saveMaintenance = (form) => {
    const value = parseFloat(form.value) || 0;
    const id = uuidv4();
    if (maintenanceModal?.id) {
      dispatch({ type: 'UPDATE_MAINTENANCE', payload: { ...maintenanceModal, ...form, value } });
    } else {
      dispatch({ type: 'ADD_MAINTENANCE', payload: { id, ...form, value } });
      if (form.createTx && value > 0) {
        const asset = vehicles.find(v => v.id === form.vehicleId);
        const km = kindMeta(kindOf(asset));
        dispatch({
          type: 'ADD_TRANSACTION',
          payload: {
            description: `${asset?.name || km.label} — ${form.description}`,
            value,
            type: 'expense',
            date: form.date,
            accountId: form.accountId,
            categoryId: form.categoryId,
            status: 'paid',
            recurrent: false,
            notes: `Manutenção de ${km.label.toLowerCase()} registrada automaticamente`,
          },
        });
      }
    }
    setMaintenanceModal(null);
  };

  const deleteAsset = (id) => dispatch({ type: 'DELETE_VEHICLE', payload: id });
  const deleteMaintenance = (id) => dispatch({ type: 'DELETE_MAINTENANCE', payload: id });

  const hasVehicles = vehicles.some(v => kindOf(v) === 'vehicle');
  const hasResidences = vehicles.some(v => kindOf(v) === 'residence');
  const visibleAssets = filter === 'all' ? vehicles : vehicles.filter(v => kindOf(v) === filter);

  const totalAssets = vehicles.length;
  const totalMaintRecords = vehicleMaintenance.length;
  const totalSpent = vehicleMaintenance.reduce((s, m) => s + (parseFloat(m.value) || 0), 0);
  const upcoming = vehicleMaintenance
    .filter(m => m.nextDate)
    .map(m => ({ ...m, daysLeft: daysUntil(m.nextDate) }))
    .filter(m => m.daysLeft !== null && m.daysLeft >= 0 && m.daysLeft <= 30)
    .sort((a, b) => a.daysLeft - b.daysLeft);

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Manutenções</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Veículos e residência — histórico e lembretes</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {vehicles.length > 0 && (
            <button onClick={() => setMaintenanceModal({})} className="btn btn-ghost" style={{ fontSize: 13 }}>
              <i className="fas fa-wrench"></i> Nova Manutenção
            </button>
          )}
          <button onClick={() => setAssetModal({ defaultKind: 'vehicle' })} className="btn btn-ghost" style={{ fontSize: 13 }}>
            <i className="fas fa-car"></i> Veículo
          </button>
          <button onClick={() => setAssetModal({ defaultKind: 'residence' })} className="btn btn-primary" style={{ fontSize: 13 }}>
            <i className="fas fa-house"></i> Residência
          </button>
        </div>
      </div>

      {/* Cards de resumo */}
      {totalAssets > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px' }}>
            <div className="metric-label">Bens cadastrados</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 4 }}>{totalAssets}</div>
          </div>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px' }}>
            <div className="metric-label">Manutenções</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)', marginTop: 4 }}>{totalMaintRecords}</div>
          </div>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px' }}>
            <div className="metric-label">Total gasto</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--red)', marginTop: 4 }}>R$ {formatCurrency(totalSpent)}</div>
          </div>
        </div>
      )}

      {/* Alerta de próximas manutenções */}
      {upcoming.length > 0 && (
        <div style={{ background: 'rgba(var(--gold-rgb),0.1)', border: '1px solid rgba(var(--gold-rgb),0.35)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <i className="fas fa-triangle-exclamation" style={{ color: 'var(--gold)', marginTop: 2, flexShrink: 0 }}></i>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>Manutenções próximas (30 dias)</div>
            {upcoming.map(m => {
              const a = vehicles.find(x => x.id === m.vehicleId);
              return (
                <div key={m.id} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>
                  <i className={`fas ${typeMeta(kindOf(a), a?.type).icon}`} style={{ marginRight: 6, color: 'var(--muted)' }}></i>
                  <strong>{a?.name}</strong> — {m.description} em {formatDate(m.nextDate)}
                  {m.daysLeft === 0 ? ' (hoje!)' : ` (${m.daysLeft}d)`}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtro de tipo — só aparece quando há os dois */}
      {hasVehicles && hasResidences && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {[
            { id: 'all', label: 'Todos' },
            { id: 'vehicle', label: '🚗 Veículos' },
            { id: 'residence', label: '🏠 Residência' },
          ].map(f => (
            <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(f.id)} style={{ fontSize: 12, padding: '5px 12px' }}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Lista de bens */}
      {vehicles.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔧</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Nenhum bem cadastrado</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24, maxWidth: 340, margin: '0 auto 24px' }}>
            Cadastre seus veículos e sua residência para controlar o histórico de manutenções e receber lembretes automáticos.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => setAssetModal({ defaultKind: 'vehicle' })} className="btn btn-ghost"><i className="fas fa-car"></i> Veículo</button>
            <button onClick={() => setAssetModal({ defaultKind: 'residence' })} className="btn btn-primary"><i className="fas fa-house"></i> Residência</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {visibleAssets.map(a => (
            <AssetCard
              key={a.id}
              asset={a}
              maintenance={vehicleMaintenance}
              onEdit={asset => setAssetModal({ asset })}
              onDelete={deleteAsset}
              onAddMaintenance={aid => setMaintenanceModal({ vehicleId: aid })}
              onDeleteMaintenance={deleteMaintenance}
            />
          ))}
        </div>
      )}

      {/* Modais */}
      {assetModal !== null && (
        <AssetModal
          asset={assetModal.asset || null}
          defaultKind={assetModal.defaultKind}
          onSave={saveAsset}
          onClose={() => setAssetModal(null)}
        />
      )}
      {maintenanceModal !== null && (
        <MaintenanceModal
          maintenance={maintenanceModal?.id ? maintenanceModal : null}
          assetId={maintenanceModal?.vehicleId || null}
          assets={vehicles}
          accounts={accounts}
          categories={categories}
          onSave={saveMaintenance}
          onClose={() => setMaintenanceModal(null)}
        />
      )}
    </div>
  );
}
